import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, User, Tag, ArrowLeft, ArrowRight } from "lucide-react";
import PageShell from "../components/PageShell";
import MarkdownLite from "../lib/MarkdownLite";
import EnquireModal from "../components/EnquireModal";
import { Button } from "../components/ui/button";
import { useContent } from "../contexts/ContentContext";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { content } = useContent();
  const [open, setOpen] = useState(false);
  const posts = content.blogPosts || [];
  const post = posts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const idx = posts.findIndex((p) => p.slug === slug);
  const prev = idx > 0 ? posts[idx - 1] : null;
  const next = idx >= 0 && idx < posts.length - 1 ? posts[idx + 1] : null;

  return (
    <>
      <PageShell
        title={post.title}
        subtitle={
          <span className="inline-flex items-center gap-4 text-sm text-emerald-100">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {formatDate(post.publishedAt)}
            </span>
            {post.author ? (
              <span className="inline-flex items-center gap-1">
                <User className="w-4 h-4" /> {post.author}
              </span>
            ) : null}
            {post.category ? (
              <span className="inline-flex items-center gap-1">
                <Tag className="w-4 h-4" /> {post.category}
              </span>
            ) : null}
          </span>
        }
        onEnquire={() => setOpen(true)}
      >
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0a4f47] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>

        {post.cover && (
          <div className="aspect-video w-full rounded-2xl overflow-hidden mb-8 shadow-md">
            <img
              src={post.cover}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <MarkdownLite text={post.content} />
        </article>

        <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#0a4f47] to-[#0e6e63] text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-xl font-bold">Ready to go solar?</div>
            <div className="text-emerald-100 text-sm mt-1">
              Book a free site survey with our experts.
            </div>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-6 font-semibold"
          >
            Enquire Now
          </Button>
        </div>

        {(prev || next) && (
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {prev ? (
              <Link
                to={`/blog/${prev.slug}`}
                className="group rounded-xl border border-slate-200 p-4 hover:border-orange-300 hover:bg-orange-50/40 transition-all"
              >
                <div className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> Previous
                </div>
                <div className="mt-1 font-semibold text-slate-900 group-hover:text-[#0a4f47]">
                  {prev.title}
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={`/blog/${next.slug}`}
                className="group rounded-xl border border-slate-200 p-4 hover:border-orange-300 hover:bg-orange-50/40 transition-all text-right"
              >
                <div className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                  Next <ArrowRight className="w-3 h-3" />
                </div>
                <div className="mt-1 font-semibold text-slate-900 group-hover:text-[#0a4f47]">
                  {next.title}
                </div>
              </Link>
            ) : (
              <div />
            )}
          </div>
        )}
      </PageShell>
      <EnquireModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
